import internalAdminClient from './internalAdminClient';

// Stage 5N-2B: thin wrappers around the /api/internal/* surface.
// Identical envelope shape as the tenant API (`{ success, data, requestId }`),
// so we unwrap the same way.

function unwrap(resp) {
  return resp && resp.data && resp.data.data !== undefined
    ? resp.data.data
    : resp && resp.data;
}

export const internalAuth = {
  login: ({ email, password }) =>
    internalAdminClient
      .post('/api/internal/auth/login', { email, password })
      .then(unwrap),
  refresh: ({ refreshToken }) =>
    internalAdminClient
      .post('/api/internal/auth/refresh', { refreshToken })
      .then(unwrap),
  me: () => internalAdminClient.get('/api/internal/auth/me').then(unwrap),
  logout: () =>
    internalAdminClient.post('/api/internal/auth/logout', {}).then(unwrap)
};

export const internalAccounts = {
  // GET /api/internal/accounts/pending?limit=&offset=
  // Returns { items, total, pagination } via unwrap.
  listPending: ({ limit = 50, offset = 0 } = {}) =>
    internalAdminClient
      .get('/api/internal/accounts/pending', { params: { limit, offset } })
      .then(unwrap),
  // Stage 5N-2C: generic list. status accepts the four
  // public.tenants.account_status values.
  list: ({ status = 'pending_approval', limit = 50, offset = 0 } = {}) =>
    internalAdminClient
      .get('/api/internal/accounts', { params: { status, limit, offset } })
      .then(unwrap),
  get: (tenantId) =>
    internalAdminClient
      .get(`/api/internal/accounts/${tenantId}`)
      .then(unwrap),
  approve: (tenantId) =>
    internalAdminClient
      .post(`/api/internal/accounts/${tenantId}/approve`, {})
      .then(unwrap),
  // body: { reason }
  reject: (tenantId, { reason }) =>
    internalAdminClient
      .post(`/api/internal/accounts/${tenantId}/reject`, { reason })
      .then(unwrap),
  suspend: (tenantId, { reason } = {}) =>
    internalAdminClient
      .post(`/api/internal/accounts/${tenantId}/suspend`, reason ? { reason } : {})
      .then(unwrap),
  reactivate: (tenantId) =>
    internalAdminClient
      .post(`/api/internal/accounts/${tenantId}/reactivate`, {})
      .then(unwrap),
  // Stage 5N-10: SEZ approve / reject. Independent of account_status —
  // see services/internalAdmin/accounts.service.js for the state machine.
  approveSez: (tenantId) =>
    internalAdminClient
      .post(`/api/internal/accounts/${tenantId}/sez/approve`, {})
      .then(unwrap),
  // body: { reason }
  rejectSez: (tenantId, { reason }) =>
    internalAdminClient
      .post(`/api/internal/accounts/${tenantId}/sez/reject`, { reason })
      .then(unwrap)
};

// Stage 5N-12: SEZ pending-signup admin surface. SEZ customers can't
// pay until the admin both approves them AND commits a tax decision
// (regular_taxable @ 18% / sez_zero_rated @ 0%). These endpoints drive
// that gate from the operator UI.
export const internalPendingSignups = {
  listSez: ({ status, limit = 50, offset = 0 } = {}) => {
    const params = { limit, offset };
    if (status) params.status = status;
    return internalAdminClient
      .get('/api/internal/pending-signups/sez', { params })
      .then(unwrap);
  },
  get: (pendingSignupId) =>
    internalAdminClient
      .get(`/api/internal/pending-signups/${pendingSignupId}`)
      .then(unwrap),
  // body: { taxTreatment: 'regular_taxable'|'sez_zero_rated', taxPercent, note? }
  approveSezPayment: (pendingSignupId, body) =>
    internalAdminClient
      .post(
        `/api/internal/pending-signups/${pendingSignupId}/sez/approve-payment`,
        body
      )
      .then(unwrap),
  // body: { reason }
  rejectSezPayment: (pendingSignupId, { reason }) =>
    internalAdminClient
      .post(
        `/api/internal/pending-signups/${pendingSignupId}/sez/reject-payment`,
        { reason }
      )
      .then(unwrap)
};

// Stage 5N-2C: audit-log listing for the /internal/audit page.
// Backend returns { items, total, pagination }. metadata is already
// sanitised at write time — safe to display.
export const internalAudit = {
  list: ({ limit = 50, offset = 0, action } = {}) => {
    const params = { limit, offset };
    if (action) params.action = action;
    return internalAdminClient
      .get('/api/internal/audit', { params })
      .then(unwrap);
  }
};

// Stage 5N-3: Custom Plan management. The backend treats each Custom
// Plan as a (purchase_plans + plan_coupons) pair under the hood; the
// API surfaces them as a single entity keyed by plan_coupons.id.
export const internalCustomPlans = {
  // status: active | inactive | expired | all
  // q:      search by code/name/GSTIN/email
  list: ({ status = 'all', q = '', limit = 50, offset = 0 } = {}) => {
    const params = { status, limit, offset };
    if (q) params.q = q;
    return internalAdminClient
      .get('/api/internal/custom-plans', { params })
      .then(unwrap);
  },
  get: (id) =>
    internalAdminClient.get(`/api/internal/custom-plans/${id}`).then(unwrap),
  create: (body) =>
    internalAdminClient.post('/api/internal/custom-plans', body).then(unwrap),
  update: (id, body) =>
    internalAdminClient.patch(`/api/internal/custom-plans/${id}`, body).then(unwrap),
  activate: (id) =>
    internalAdminClient
      .post(`/api/internal/custom-plans/${id}/activate`, {})
      .then(unwrap),
  deactivate: (id, { reason }) =>
    internalAdminClient
      .post(`/api/internal/custom-plans/${id}/deactivate`, { reason })
      .then(unwrap),
  listRedemptions: (id, { limit = 50, offset = 0 } = {}) =>
    internalAdminClient
      .get(`/api/internal/custom-plans/${id}/redemptions`, {
        params: { limit, offset }
      })
      .then(unwrap)
};

// Stage 5N-4: GST master + Standard Plan pricing.
export const internalGstRates = {
  list: () =>
    internalAdminClient.get('/api/internal/gst-rates').then(unwrap),
  create: (body) =>
    internalAdminClient.post('/api/internal/gst-rates', body).then(unwrap),
  update: (id, body) =>
    internalAdminClient.patch(`/api/internal/gst-rates/${id}`, body).then(unwrap),
  setDefault: (id) =>
    internalAdminClient
      .post(`/api/internal/gst-rates/${id}/set-default`, {})
      .then(unwrap),
  deactivate: (id) =>
    internalAdminClient
      .post(`/api/internal/gst-rates/${id}/deactivate`, {})
      .then(unwrap)
};

// Stage 5N-9A — internal-admin subscription invoice access.
//   viewReport(id) — primary flow, returns text/html for the in-app
//                    Crystal-Reports-style viewer.
//   PDF download is intentionally not exposed today; the backend
//   /api/internal/invoices/:id/pdf route is kept for ops probes only.
export const internalInvoices = {
  viewReport: (invoiceId) =>
    internalAdminClient
      .get(`/api/internal/invoices/${invoiceId}/report`, {
        responseType: 'text',
        headers: { Accept: 'text/html' },
        transformResponse: [(d) => d]
      })
      .then((resp) => resp.data)
};

export const internalStandardPricing = {
  get: () =>
    internalAdminClient.get('/api/internal/pricing/standard').then(unwrap),
  history: () =>
    internalAdminClient
      .get('/api/internal/pricing/standard/history')
      .then(unwrap),
  update: (body) =>
    internalAdminClient
      .patch('/api/internal/pricing/standard', body)
      .then(unwrap),
  preview: ({ userCount = 1, billingCycle = 'monthly' } = {}) =>
    internalAdminClient
      .get('/api/internal/pricing/standard/preview', {
        params: { userCount, billingCycle }
      })
      .then(unwrap)
};

// Stage R2 — internal-admin report definition CRUD.
//   list()                  -> { definitions: [...] }
//   get(id)                 -> { definition }
//   create(body)            -> { definition }
//   patch(id, body)         -> { definition }
//   enable(id) / disable(id)-> { definition }
// `manage` permission is super_admin-only by default; `view` is broad.
export const internalReportDefinitions = {
  list: () =>
    internalAdminClient
      .get('/api/internal/reports/definitions')
      .then(unwrap),
  get: (id) =>
    internalAdminClient
      .get(`/api/internal/reports/definitions/${id}`)
      .then(unwrap),
  create: (body) =>
    internalAdminClient
      .post('/api/internal/reports/definitions', body)
      .then(unwrap),
  patch: (id, body) =>
    internalAdminClient
      .patch(`/api/internal/reports/definitions/${id}`, body)
      .then(unwrap),
  enable: (id) =>
    internalAdminClient
      .post(`/api/internal/reports/definitions/${id}/enable`, {})
      .then(unwrap),
  disable: (id) =>
    internalAdminClient
      .post(`/api/internal/reports/definitions/${id}/disable`, {})
      .then(unwrap)
};

// Stage 5N-CLONE — replicate one tenant's configuration/pricing masters
// onto another. super_admin-only (permission 'tenants.clone_config').
export const internalTenantClone = {
  // → [{ tenantId, displayName, gstin, status }]
  listTenants: () =>
    internalAdminClient
      .get('/api/internal/tenant-clone/tenants')
      .then(unwrap),
  // → [{ id, label, description, tables:[...] }]
  listSections: () =>
    internalAdminClient
      .get('/api/internal/tenant-clone/sections')
      .then(unwrap),
  // body: { sourceTenantId, targetTenantId, sections? }
  preview: (body) =>
    internalAdminClient
      .post('/api/internal/tenant-clone/preview', body)
      .then(unwrap),
  apply: (body) =>
    internalAdminClient
      .post('/api/internal/tenant-clone/apply', body)
      .then(unwrap)
};

// Stage SCAN-1 — ESP32 QR scanner fleet: hardware inventory + tenant
// allocation. Assigning a scanner to a DEPARTMENT is deliberately absent
// here — that is the tenant master's decision and lives on the tenant-facing
// /api/erp/production/scan-devices surface.
//
// register, registerBulk and rotateKey return a RAW device_key exactly once.
// Never persist it in component state longer than the modal that shows it,
// and never log it.
export const internalScanDevices = {
  // params: { allocationStatus?, tenantId?, search?, limit?, offset? }
  list: (params = {}) =>
    internalAdminClient
      .get('/api/internal/scan-devices', { params })
      .then(unwrap),
  get: (id) =>
    internalAdminClient.get(`/api/internal/scan-devices/${id}`).then(unwrap),
  // body: { hardwareRev? } → { deviceId, deviceKey, keyShownOnce, notice }
  register: (body = {}) =>
    internalAdminClient.post('/api/internal/scan-devices', body).then(unwrap),
  // body: { count, hardwareRev? } → { count, devices: [{deviceId, deviceKey}] }
  registerBulk: (body) =>
    internalAdminClient.post('/api/internal/scan-devices/bulk', body).then(unwrap),
  // Raw CSV for the flashing bench. Returns text, not the JSON envelope, so
  // it bypasses unwrap.
  registerBulkCsv: (body) =>
    internalAdminClient
      .post('/api/internal/scan-devices/bulk?format=csv', body, { responseType: 'text' })
      .then((r) => r.data),
  allocate: (id, { tenantId }) =>
    internalAdminClient
      .post(`/api/internal/scan-devices/${id}/allocate`, { tenantId })
      .then(unwrap),
  // retire:true ends the device's service life instead of returning it to stock.
  deallocate: (id, { retire = false } = {}) =>
    internalAdminClient
      .post(`/api/internal/scan-devices/${id}/deallocate`, { retire })
      .then(unwrap),
  rotateKey: (id) =>
    internalAdminClient
      .post(`/api/internal/scan-devices/${id}/rotate-key`, {})
      .then(unwrap),
  retire: (id) =>
    internalAdminClient
      .post(`/api/internal/scan-devices/${id}/retire`, {})
      .then(unwrap)
};

export default {
  internalAuth,
  internalAccounts,
  internalAudit,
  internalCustomPlans,
  internalGstRates,
  internalStandardPricing,
  internalInvoices,
  internalReportDefinitions,
  internalTenantClone,
  internalScanDevices
};
